import axios from "axios";
import { fetchCsrfToken, getCsrfToken } from "./csrfService";

// Determine the base URL dynamically
const getBaseUrl = () => {
  const protocol = window.location.protocol;
  
  if (process.env.NODE_ENV === 'development') {
    if (process.env.REACT_APP_API_URL) {
      const url = process.env.REACT_APP_API_URL;
      return url.startsWith('http') ? `${url}/api` : `${protocol}//${url}/api`;
    }
    
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}:5000/api`;
  }
  
  return "/api";
};

const baseUrl = getBaseUrl();

// Create the axios instance
const api = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    headers: { 
      "Content-Type": "application/json"
    }
});

// Request interceptor: attach the latest CSRF token to headers
api.interceptors.request.use(
    async (config) => {
      // Only add CSRF token for mutation operations
      if (["post", "put", "delete", "patch"].includes(config.method)) {
        let csrfToken = getCsrfToken();
        
        if (!csrfToken) {
          try {
            csrfToken = await fetchCsrfToken();
          } catch (error) {
            // Failed to fetch CSRF token
          }
        }
        
        if (csrfToken) {
          config.headers["X-CSRFToken"] = csrfToken;
        }
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

// Function to handle CSRF error
const handleCsrfError = async (error) => {
    if (!error.response) {
      throw error;
    }
    
    // If error is related to CSRF token
    if (
      (error.response.status === 400 && error.response.data?.message?.includes("CSRF")) ||
      (error.response.status === 403 && error.response.data?.error?.includes("CSRF"))
    ) {
        try {
            const newToken = await fetchCsrfToken();
            
            // Retry the original request with the new token
            const originalRequest = error.config;
            originalRequest.headers["X-CSRFToken"] = newToken;
            
            return axios(originalRequest);
        } catch (tokenError) {
            throw error;
        }
    }
    
    throw error;
};

// Response interceptor to retry requests if CSRF token is expired
api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
        try {
            return await handleCsrfError(error);
        } catch (rethrownError) {
            return Promise.reject(rethrownError);
        }
    }
);

// API helper methods that use the configured axios instance
const apiService = {
  // Auth endpoints
  login: (credentials) => {
    return api.post('/login', credentials);
  },
  
  logout: () => {
    return api.post('/logout');
  },
  
  register: (userData) => {
    return api.post('/register', userData);
  },
  
  getProfile: () => {
    return api.get('/profile');
  },
  
  updateProfile: (profileData) => {
    return api.put('/profile', profileData);
  },
  
  // Community endpoints
  getCommunities: () => {
    return api.get('/communities');
  },
  
  // Events endpoints
  getEvents: (date) => {
    return api.get('/events', { params: { date } });
  },
  
  createEvent: (eventData) => {
    return api.post('/events', eventData);
  },
  
  // Custom request method for flexibility
  request: (config) => {
    return api(config);
  }
};

// Export both the configured axios instance and the helper methods
export default api;
export { apiService };